import { Injectable } from '@angular/core';
import { MediaUnlockService } from '@app/shared/services/media-unlock.service';

@Injectable({
    providedIn: 'root'
})
export class MediaPlaybackService {
    private panelVideos: HTMLVideoElement[] = [];
    private featureVideos: HTMLVideoElement[] = [];
    private ambientAudio?: HTMLAudioElement;
    private flyAudio?: HTMLAudioElement;
    private notificationAudio: Partial<Record<'success' | 'error' | 'info' | 'warning', HTMLAudioElement>> = {};
    private notificationDefaultsReady = false;
    private activePanel = -1;
    private mediaReady = false;
    private audioReady = false;
    private notificationReady = false;
    private mediaUnlockBound = false;
    private audioUnlockBound = false;
    private notificationUnlockBound = false;
    private mediaUnlockCleanup?: () => void;
    private audioUnlockCleanup?: () => void;
    private panelPlaybackLocked = false;
    private panelPlaybackUnlockTimer?: ReturnType<typeof setTimeout>;
    private exclusiveFeatureVideo?: HTMLVideoElement;
    private landingPageVisible = true;
    private panelPlaybackVisible = true;
    private panelAutoplayEnabled = true;
    private readonly onLandingVideoPlay = (event: Event) => {
        const activeVideo = event.currentTarget as HTMLVideoElement | null;
        if (!activeVideo) {
            return;
        }

        const isPanelVideo = this.panelVideos.includes(activeVideo);
        if (!this.landingPageVisible || (isPanelVideo && (!this.panelPlaybackVisible || !this.panelAutoplayEnabled))) {
            activeVideo.pause();
            return;
        }

        [...this.panelVideos, ...this.featureVideos].forEach((video) => {
            if (video !== activeVideo) {
                video.pause();
            }
        });

        this.exclusiveFeatureVideo = this.featureVideos.includes(activeVideo)
            ? activeVideo
            : undefined;
    };

    constructor(private readonly mediaUnlock: MediaUnlockService) { }

    handleUserInteraction(): void {
        this.mediaUnlock.attemptUnlock();
    }

    registerPanelVideos(videos: HTMLVideoElement[]): void {
        this.unbindLandingVideoPlayListeners(this.panelVideos);
        this.mediaUnlock.unregisterMedia(this.panelVideos);
        this.panelVideos = videos ?? [];
        this.panelVideos.forEach((video) => {
            video.muted = true;
            video.volume = 0;
        });
        this.bindLandingVideoPlayListeners(this.panelVideos);
        this.mediaUnlock.registerMedia(this.panelVideos);
        this.bindMediaUnlock();
        this.syncPanelPlayback();
    }

    registerFeatureVideos(videos: HTMLVideoElement[]): void {
        this.unbindLandingVideoPlayListeners(this.featureVideos);
        this.mediaUnlock.unregisterMedia(this.featureVideos);
        this.featureVideos = videos ?? [];
        if (this.exclusiveFeatureVideo && !this.featureVideos.includes(this.exclusiveFeatureVideo)) {
            this.exclusiveFeatureVideo = undefined;
        }
        this.featureVideos.forEach((video) => {
            video.muted = true;
            video.volume = 0;
        });
        this.bindLandingVideoPlayListeners(this.featureVideos);
        this.mediaUnlock.registerMedia(this.featureVideos);
        this.bindMediaUnlock();
    }

    claimExclusiveFeatureVideo(video: HTMLVideoElement): void {
        if (this.exclusiveFeatureVideo && this.exclusiveFeatureVideo !== video) {
            this.exclusiveFeatureVideo.pause();
        }

        this.exclusiveFeatureVideo = video;
        this.panelVideos.forEach((panelVideo) => panelVideo.pause());
        this.featureVideos.forEach((featureVideo) => {
            if (featureVideo !== video) {
                featureVideo.pause();
            }
        });
    }

    releaseExclusiveFeatureVideo(video: HTMLVideoElement): void {
        video.pause();
        if (this.exclusiveFeatureVideo !== video) {
            return;
        }

        this.exclusiveFeatureVideo = undefined;
        this.syncPanelPlayback();
    }

    setLandingPageVisible(visible: boolean): void {
        this.landingPageVisible = visible;
        if (!visible) {
            this.panelVideos.forEach((video) => video.pause());
            this.featureVideos.forEach((video) => video.pause());
            return;
        }

        const featureVideo = this.exclusiveFeatureVideo;
        if (featureVideo && this.featureVideos.includes(featureVideo)) {
            featureVideo.defaultMuted = true;
            featureVideo.muted = true;
            featureVideo.play().catch(() => {
                // Autoplay may still be blocked until user interaction.
            });
            return;
        }

        this.syncPanelPlayback();
    }

    setPanelPlaybackVisible(visible: boolean): void {
        this.panelPlaybackVisible = visible;
        this.syncPanelPlayback();
    }

    setPanelAutoplayEnabled(enabled: boolean): void {
        this.panelAutoplayEnabled = enabled;
        this.syncPanelPlayback();
    }

    registerAudio(ambient?: HTMLAudioElement, fly?: HTMLAudioElement): void {
        this.mediaUnlock.unregisterMedia([this.ambientAudio, this.flyAudio]);
        this.ambientAudio = ambient;
        this.flyAudio = fly;

        if (this.ambientAudio) {
            this.ambientAudio.preload = 'auto';
            this.ambientAudio.loop = true;
            this.ambientAudio.volume = 0.22;
        }

        if (this.flyAudio) {
            this.flyAudio.preload = 'auto';
            this.flyAudio.volume = 0.6;
        }

        this.mediaUnlock.registerMedia([this.ambientAudio, this.flyAudio]);
        this.bindAudioUnlock();
    }

    registerNotificationSounds(sounds: Partial<Record<'success' | 'error' | 'info' | 'warning', HTMLAudioElement>>): void {
        this.notificationAudio = { ...this.notificationAudio, ...sounds };
        const media = Object.values(this.notificationAudio).filter(Boolean) as HTMLAudioElement[];
        if (media.length > 0) {
            media.forEach((audio) => {
                audio.preload = 'auto';
                audio.loop = false;
            });
            this.mediaUnlock.registerMedia(media);
            this.bindNotificationUnlock();
        }
    }

    playNotificationSound(type: 'success' | 'error' | 'info' | 'warning', playCount: number = 1): void {
        if (!Object.values(this.notificationAudio).some(Boolean)) {
            this.ensureNotificationDefaults();
        }
        if (!this.notificationReady) {
            return;
        }

        const fallback = this.notificationAudio.success ?? this.notificationAudio.error;
        const audio = this.notificationAudio[type] ?? fallback;
        if (!audio) {
            return;
        }

        if (playCount <= 0) {
            audio.loop = true;
            audio.currentTime = 0;
            audio.play().catch(() => {
                // Autoplay may still be blocked until user interaction.
            });
            return;
        }

        audio.loop = false;
        let counter = 0;
        const playOnce = () => {
            audio.currentTime = 0;
            audio.play().catch(() => {
                // Autoplay may still be blocked until user interaction.
            });
        };
        audio.onended = () => {
            counter += 1;
            if (counter < playCount) {
                playOnce();
            } else {
                audio.onended = null;
            }
        };
        playOnce();
    }

    setActivePanel(panel: number): void {
        this.activePanel = panel;
        this.syncPanelPlayback();
    }

    playFlySound(): void {
        if (!this.audioReady || !this.flyAudio) {
            return;
        }

        this.flyAudio.currentTime = 0;
        this.flyAudio.play().catch(() => {
            // Autoplay may still be blocked until user interaction.
        });
    }

    pausePanelPlayback(durationMs?: number): void {
        this.panelPlaybackLocked = true;
        this.panelVideos.forEach((video) => video.pause());
        if (this.panelPlaybackUnlockTimer) {
            clearTimeout(this.panelPlaybackUnlockTimer);
            this.panelPlaybackUnlockTimer = undefined;
        }
        if (typeof durationMs === 'number' && durationMs > 0) {
            this.panelPlaybackUnlockTimer = setTimeout(() => {
                this.panelPlaybackLocked = false;
                this.panelPlaybackUnlockTimer = undefined;
                this.syncPanelPlayback();
            }, durationMs);
        }
    }

    resumePanelPlayback(): void {
        if (!this.panelPlaybackLocked) {
            return;
        }
        if (this.panelPlaybackUnlockTimer) {
            clearTimeout(this.panelPlaybackUnlockTimer);
            this.panelPlaybackUnlockTimer = undefined;
        }
        this.panelPlaybackLocked = false;
        this.syncPanelPlayback();
    }

    pauseAmbient(): void {
        if (this.ambientAudio) {
            this.ambientAudio.pause();
        }
    }

    resumeAmbient(): void {
        if (!this.audioReady || !this.ambientAudio) {
            return;
        }

        if (this.ambientAudio.paused) {
            this.ambientAudio.play().catch(() => {
                // Autoplay may still be blocked until user interaction.
            });
        }
    }

    stopAll(): void {
        this.panelVideos.forEach((video) => video.pause());
        this.featureVideos.forEach((video) => video.pause());
        this.exclusiveFeatureVideo = undefined;
        if (this.panelPlaybackUnlockTimer) {
            clearTimeout(this.panelPlaybackUnlockTimer);
            this.panelPlaybackUnlockTimer = undefined;
        }
        this.panelPlaybackLocked = false;
        if (this.ambientAudio) {
            this.ambientAudio.pause();
            this.ambientAudio.currentTime = 0;
        }
        if (this.flyAudio) {
            this.flyAudio.pause();
            this.flyAudio.currentTime = 0;
        }
    }

    releaseLandingMedia(): void {
        const landingMedia = [
            ...this.panelVideos,
            ...this.featureVideos,
            this.ambientAudio,
            this.flyAudio
        ].filter((element): element is HTMLMediaElement => !!element);

        this.unbindLandingVideoPlayListeners(this.panelVideos);
        this.unbindLandingVideoPlayListeners(this.featureVideos);
        landingMedia.forEach((element) => {
            element.pause();
            element.onended = null;
            try {
                element.currentTime = 0;
            } catch {
                // Metadata may not be available for an element being detached.
            }

            element.removeAttribute('src');
            element.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
            element.load();
        });

        this.mediaUnlock.unregisterMedia(landingMedia);
        this.mediaUnlockCleanup?.();
        this.audioUnlockCleanup?.();
        this.mediaUnlockCleanup = undefined;
        this.audioUnlockCleanup = undefined;
        this.mediaUnlockBound = false;
        this.audioUnlockBound = false;
        this.mediaReady = false;
        this.audioReady = false;
        this.panelVideos = [];
        this.featureVideos = [];
        this.ambientAudio = undefined;
        this.flyAudio = undefined;
        this.activePanel = -1;
        this.exclusiveFeatureVideo = undefined;
        this.landingPageVisible = true;
        this.panelPlaybackVisible = true;

        if (this.panelPlaybackUnlockTimer) {
            clearTimeout(this.panelPlaybackUnlockTimer);
            this.panelPlaybackUnlockTimer = undefined;
        }
        this.panelPlaybackLocked = false;
    }

    private bindMediaUnlock(): void {
        if (this.mediaUnlockBound) {
            return;
        }

        if (this.mediaUnlock.isUnlocked()) {
            this.mediaReady = true;
            this.syncPanelPlayback();
            return;
        }

        this.mediaUnlockBound = true;
        this.mediaUnlockCleanup = this.mediaUnlock.onUnlock(() => {
            this.mediaUnlockCleanup = undefined;
            if (this.mediaReady) {
                return;
            }
            this.mediaReady = true;
            this.syncPanelPlayback();
        });
    }

    private bindAudioUnlock(): void {
        if (this.audioUnlockBound) {
            return;
        }

        if (this.mediaUnlock.isUnlocked()) {
            this.audioReady = true;
            this.resumeAmbient();
            return;
        }

        this.audioUnlockBound = true;
        this.audioUnlockCleanup = this.mediaUnlock.onUnlock(() => {
            this.audioUnlockCleanup = undefined;
            if (this.audioReady) {
                return;
            }
            this.audioReady = true;
            this.resumeAmbient();
        });
    }

    private bindNotificationUnlock(): void {
        if (this.notificationUnlockBound) {
            return;
        }
        this.notificationUnlockBound = true;
        this.mediaUnlock.onUnlock(() => {
            if (this.notificationReady) {
                return;
            }
            this.notificationReady = true;
        });
    }

    private ensureNotificationDefaults(): void {
        if (this.notificationDefaultsReady) {
            return;
        }

        const successSound = new Audio('assets/sounds/success.mp3');
        const infoSound = new Audio('assets/sounds/message.mp3');
        const warningSound = new Audio('assets/sounds/message.mp3');
        const errorSound = new Audio('assets/sounds/error.mp3');

        successSound.volume = 0.75;
        infoSound.volume = 0.72;
        warningSound.volume = 0.75;
        errorSound.volume = 0.75;

        this.registerNotificationSounds({
            success: successSound,
            info: infoSound,
            warning: warningSound,
            error: errorSound
        });

        this.notificationDefaultsReady = true;
    }

    private syncPanelPlayback(): void {
        if (
            !this.mediaReady
            || this.panelPlaybackLocked
            || !this.landingPageVisible
            || !this.panelPlaybackVisible
            || !this.panelAutoplayEnabled
            || !!this.exclusiveFeatureVideo
        ) {
            this.panelVideos.forEach((video) => video.pause());
            return;
        }

        this.panelVideos.forEach((video) => {
            const panelValue = Number(video.dataset.panel ?? -1);
            if (panelValue !== this.activePanel) {
                video.pause();
                return;
            }
            video.loop = true;
            video.defaultMuted = true;
            video.muted = true;
            video.play().catch(() => {
                // Autoplay may still be blocked until user interaction.
            });
        });
    }

    private bindLandingVideoPlayListeners(videos: HTMLVideoElement[]): void {
        videos.forEach((video) => video.addEventListener('play', this.onLandingVideoPlay));
    }

    private unbindLandingVideoPlayListeners(videos: HTMLVideoElement[]): void {
        videos.forEach((video) => video.removeEventListener('play', this.onLandingVideoPlay));
    }
}
